import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useDemo } from '../../contexts/DemoContext';
import {
  getGoalById,
  updateGoalRating,
  submitEmployeeFinalReview,
  isEmployeeFinalReviewUnlocked,
  getFinalReviewUnlockDate,
  isSelfReviewOpenByDate,
  isGlobalSelfReviewOpen
} from '../../services/goalsService';
import {
  isDemoGoalId,
  getDemoGoalById,
  updateDemoGoalGoals,
  submitDemoSelfReview
} from '../../services/demoData';
import { GoalDocument, Goal } from '../../types';
import {
  ArrowLeft,
  Send,
  AlertCircle,
  Target,
  Lock,
  Calendar,
  FlaskConical
} from 'lucide-react';

type RatingOption = 'A' | 'B' | 'NI';

const ratingOptions: { value: RatingOption; label: string; color: string }[] = [
  { value: 'A', label: 'At/Above Expectations', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'B', label: 'Below Expectations', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
  { value: 'NI', label: 'Needs Improvement', color: 'bg-red-100 text-red-700 border-red-300' },
];

const goalTypeLabels: Record<string, string> = {
  normal: 'Normal Goal',
  stretch: 'Stretch Goal',
  hard: 'Hard Goal',
};

const EmployeeFinalReview = () => {
  const { goalId } = useParams<{ goalId: string }>();
  const { user, isDeveloper } = useAuth();
  const { refreshDemo } = useDemo();
  const navigate = useNavigate();
  const [goal, setGoal] = useState<GoalDocument | null>(null);
  const [localGoals, setLocalGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [unlockDate, setUnlockDate] = useState<string>('');

  // Check if this is a demo goal
  const isDemo = goalId ? isDemoGoalId(goalId) : false;

  useEffect(() => {
    const fetchGoal = async () => {
      if (!goalId) return;
      try {
        let goalDoc: GoalDocument | null = null;

        if (isDemo) {
          // Demo mode - fetch from in-memory state
          goalDoc = getDemoGoalById(goalId);
          // Self-review always unlocked in demo mode
          setIsUnlocked(true);
        } else {
          goalDoc = await getGoalById(goalId);
          if (goalDoc) {
            // Self-review is unlocked if:
            // 1. Date-based unlock (quarter ended) OR
            // 2. selfReviewOpenDate has passed (manager/admin override) OR
            // 3. Global self-review is open for this quarter OR
            // 4. User is a developer
            const dateUnlocked = isEmployeeFinalReviewUnlocked(goalDoc.quarter, goalDoc.year, isDeveloper);
            const overrideOpen = isSelfReviewOpenByDate(goalDoc);
            const globalOpen = await isGlobalSelfReviewOpen(goalDoc.quarter, goalDoc.year);
            setIsUnlocked(dateUnlocked || overrideOpen || globalOpen);
            setUnlockDate(getFinalReviewUnlockDate(goalDoc.quarter, goalDoc.year));
          }
        }

        setGoal(goalDoc);
        if (goalDoc) {
          setLocalGoals([...goalDoc.goals]);
        }
      } catch (error) {
        console.error('Error fetching goal:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoal();
  }, [goalId, isDeveloper, isDemo]);

  const handleRatingChange = async (goalIndex: number, rating: RatingOption) => {
    if (!goalId || !goal || !isUnlocked) return;

    // Update local state first
    const updatedGoals = [...localGoals];
    updatedGoals[goalIndex].employeeRating = rating;
    setLocalGoals(updatedGoals);

    try {
      if (isDemo) {
        // Demo mode - update in memory
        updateDemoGoalGoals(goalId, updatedGoals);
        refreshDemo();
      } else {
        await updateGoalRating(goalId, goalIndex, 'employee', rating);
      }

      // Update goal state
      setGoal({ ...goal, goals: updatedGoals });
    } catch (error) {
      console.error('Error updating rating:', error);
      setError('Failed to save rating');
      // Revert local state on error
      setLocalGoals([...goal.goals]);
    }
  };

  const handleSubmit = async () => {
    if (!goalId || !goal) return;

    // Validate all goals have ratings
    const missingRatings = localGoals.filter(g => !g.employeeRating);
    if (missingRatings.length > 0) {
      setError('Please rate all goals before submitting');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (isDemo) {
        // Demo mode - submit in memory
        const signerName = 'Training User';
        submitDemoSelfReview(goalId, localGoals, signerName);
        refreshDemo();
        navigate('/dashboard');
      } else {
        if (!user) {
          setError('You must be logged in to submit');
          setSubmitting(false);
          return;
        }
        await submitEmployeeFinalReview(goalId, user.displayName, isDeveloper);
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Error submitting review:', err);
      setError(err.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cps-primary"></div>
      </div>
    );
  }

  if (!goal) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Goal not found</p>
      </div>
    );
  }

  const allRated = localGoals.every(g => g.employeeRating);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Demo indicator */}
      {isDemo && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-800">
          <FlaskConical className="w-5 h-5" />
          <span className="font-medium">Demo Goal</span>
          <span className="text-sm">- This self-review is for training purposes only</span>
        </div>
      )}

      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Q{goal.quarter} {goal.year} Self-Review
          </h1>
          <p className="text-gray-500">
            {isDemo ? goal.userName : 'Rate your performance on each goal'}
          </p>
        </div>
      </div>

      {!isUnlocked && !isDemo && (
        <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
          <Lock className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="text-yellow-800 font-medium">Self-review not yet available</p>
            <p className="text-yellow-700 text-sm mt-1 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Available on {unlockDate}
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Goals Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Target className="w-5 h-5 text-cps-primary" />
          <h2 className="text-lg font-semibold">{isDemo ? 'Goals' : 'Your Goals'}</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Rate {isDemo ? 'the employee\'s' : 'your'} performance on each goal. Be honest in {isDemo ? 'this' : 'your'} self-assessment.
        </p>

        <div className="space-y-4">
          {localGoals.map((g, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                      {goalTypeLabels[g.type]}
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900">{g.title}</h3>
                  <p className="text-sm text-gray-600 mt-1">{g.description}</p>
                </div>

                <div className="flex gap-2">
                  {ratingOptions.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handleRatingChange(index, option.value)}
                      disabled={!isUnlocked}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-all ${
                        g.employeeRating === option.value
                          ? option.color + ' border-2'
                          : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
                      } ${!isUnlocked ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {option.value}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rating Legend */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Rating Guide</h3>
        <div className="grid grid-cols-3 gap-4 text-sm">
          {ratingOptions.map(option => (
            <div key={option.value} className="flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded ${option.color}`}>{option.value}</span>
              <span className="text-gray-600">{option.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Submit Button */}
      {isUnlocked && (
        <div className="flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={!allRated || submitting}
            className="flex items-center gap-2 px-6 py-3 bg-cps-primary hover:bg-cps-light text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Submitting...</span>
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                <span>Submit Self-Review</span>
              </>
            )}
          </button>
        </div>
      )}

      {!allRated && isUnlocked && (
        <p className="text-center text-sm text-gray-500 mt-4">
          Rate all {localGoals.filter(g => !g.employeeRating).length} remaining goal(s) to submit
        </p>
      )}
    </div>
  );
};

export default EmployeeFinalReview;